import { useAtom } from '@reatom/npm-react'
import { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { reatomFollowingsFilter } from '@root/features/user/user-connections'
import { reatomUsersSearch } from '@root/features/user/user-connections/atoms/reatom-users-search'
import * as FollowingsSearchMode from '@root/features/user/users-search/model/followings-search-mode'
import { UsersSearch } from '@root/features/user/users-search/ui/users-search'

import { GlobalSearchUsersResults } from './global-search-users-result'
import { RelationFollowings } from './relation-followings'

import type { DelugeId } from '@root/entities/user-short'

type RelationFollowingsFilterProps = {
  delugeId: DelugeId
}

export const RelationFollowingsFilter = ({
  delugeId,
}: RelationFollowingsFilterProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [searchMode, setSearchMode] =
    useState<FollowingsSearchMode.FollowingsSearchMode>('local')
  const followingsFilter = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomFollowingsFilter(delugeId),
    [delugeId],
  )

  const [searchQuery, setLocalSearchQuery] = useAtom(
    followingsFilter.searchQueryAtom,
  )

  const globalModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomUsersSearch({ debounce: 300, startSearchSymbols: 3 }),
    [],
  )
  const [, setUsersSearchQuery] = useAtom(globalModel.searchQueryAtom)

  const setSearchQuery = (newSearchQuery: string) => {
    setLocalSearchQuery(newSearchQuery)
    setUsersSearchQuery(newSearchQuery)
  }

  return (
    <Root ref={ref}>
      <Head>
        <UsersSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchMode={searchMode}
          setSearchMode={setSearchMode}
          ref={ref}
        />
      </Head>
      {FollowingsSearchMode.match(searchMode, {
        onLocal: () => (
          <RelationFollowings followingsFilter={followingsFilter} />
        ),
        onGlobal: () => <GlobalSearchUsersResults model={globalModel} />,
      })}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  padding: 0 16px 12px;
  overflow: auto;
`

const Head = styled.div`
  z-index: 1;
  position: sticky;
  top: 0;
  padding-bottom: 12px;
  background: var(--background-1);
`
